import katex from "katex";

/**
 * Normalize backslash math delimiters to dollar delimiters.
 *
 * Models frequently emit `\( ... \)` (inline) and `\[ ... \]` (display) LaTeX,
 * but remark-math only recognizes `$ ... $` / `$$ ... $$`. Converting up front
 * lets the same content render in both the chat markdown pipeline (remark-math
 * + rehype-katex) and the iframe preview (KaTeX renderToString).
 */
export const normalizeMathDelimiters = (text: string): string => {
  if (!text) return text;
  // Display first so a `\[ ... \]` isn't half-eaten by the inline pass.
  let normalized = text.replace(/\\\[([\s\S]+?)\\\]/g, (_m, tex) => `$$${tex}$$`);
  normalized = normalized.replace(/\\\(([\s\S]+?)\\\)/g, (_m, tex) => `$${tex}$`);
  return normalized;
};

/**
 * Prepare model output for the Markdown math pipeline.
 *
 * remark-math treats any pair of single dollar signs as inline math. Escape
 * currency markers such as `$400` before delimiter normalization so separate
 * prices cannot consume the prose between them as one math expression.
 */
export const prepareMathMarkdown = (text: string): string => {
  if (!text) return text;

  const currencySafe = text.replace(/(^|[^\\])\$(?=\d)/g, "$1\\$");
  return normalizeMathDelimiters(currencySafe);
};

export const escapeHtml = (s: string): string =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

// Matches $$ ... $$ (display, may span lines) and $ ... $ (inline, single line).
const MATH_SEGMENT_RE = /(\$\$[\s\S]+?\$\$|\$[^$\n]+?\$)/g;

/**
 * Render a plain-text line to HTML, converting `$...$` / `$$...$$` (and, via
 * {@link normalizeMathDelimiters}, `\(...\)` / `\[...\]`) segments to KaTeX
 * markup while HTML-escaping everything else. Used for the iframe preview,
 * where we can't run a full markdown pipeline. On a KaTeX error the original
 * (escaped) delimiter text is kept so nothing is lost.
 */
export const renderTextWithMathToHtml = (raw: string): string => {
  const text = normalizeMathDelimiters(raw);
  let out = "";
  let last = 0;
  let m: RegExpExecArray | null;
  MATH_SEGMENT_RE.lastIndex = 0;
  while ((m = MATH_SEGMENT_RE.exec(text)) !== null) {
    out += escapeHtml(text.slice(last, m.index));
    const token = m[0];
    const display = token.startsWith("$$");
    const tex = display ? token.slice(2, -2) : token.slice(1, -1);
    try {
      out += katex.renderToString(tex, {
        displayMode: display,
        throwOnError: false,
        output: "html",
      });
    } catch {
      out += escapeHtml(token);
    }
    last = MATH_SEGMENT_RE.lastIndex;
  }
  out += escapeHtml(text.slice(last));
  return out;
};

// KaTeX stylesheet (+ fonts) for iframe previews, loaded from CDN so the
// srcdoc document can resolve the font files. Kept outside the sanitized body
// (DOMPurify strips <link>), so it must be prepended to the srcDoc head.
export const KATEX_IFRAME_HEAD =
  '<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/katex@0.16.11/dist/katex.min.css" crossorigin="anonymous">';
